"use client";
import { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Heading,
  IconButton,
  Tooltip,
  useToast,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import {
  FaTruck,
  FaSync,
  FaTimes,
  FaTrash,
  FaRoute,
  FaMotorcycle,
  FaEye,
  FaUser,
  FaBox,
  FaMoneyBill,
  FaEllipsisH,
  FaFileInvoice,
} from "react-icons/fa";
import {
  Shipment,
  ShipmentStatus,
  PorterDeliveryTrackingStatus,
  VehicleType,
} from "../shipment.interface";
import {
  cancelShipment,
  deleteShipment,
  getShipment,
  getTracking,
  updateShipmentService,
  markConsignmentDeliveredService,
} from "../service";
import { GaraazCopyText } from "@svan-garaaz/reactsharedlib";
import GaraazAlertDialogue from "../../../components/GaraazAlertDialogue";
import {
  formatDateForNotification,
  formatTime,
} from "../../../utils/dateResolvers";
import { MdAccessTime } from "react-icons/md";
import TrackingModal, { TrackingDetails } from "./TrackingModal";
import { GetStatusBadge } from "./GetStatusBadge";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { isMobileConfigFlagEnabled } from "../../../utils/parseMobileConfigBooleanFlag";
import ShipmentDetailsModal from "./ShipmentDetailsModal";
import type { ShipmentDetails } from "./types";
import { mapApiShipmentToShipmentDetails } from "./mapApiShipmentToShipmentDetails";
import { formatCurrency } from "../../../utils/formatCurrency";
import { DeliveryModes } from "../../Orders/enums";
import { FcShipped } from "react-icons/fc";
import DeliveryModeBadge from "../../../components/DeliveryModeBadge";
import {
  getDeliveryStopStatus,
  isMultiStopTrip,
} from "../utils/logisticsTripUtils";

interface ShipmentsTableProps {
  shipments: Shipment[];
}

export const cancelableShipmentStatuses = [
  ShipmentStatus.CREATED,
  ShipmentStatus.REQUESTED,
  ShipmentStatus.ASSIGNED,
];

export const deletableShipmentStatuses = [
  ShipmentStatus.CREATED,
  ShipmentStatus.CANCELLED,
];

export default function ShipmentsTable({
  shipments: initialShipments,
}: ShipmentsTableProps) {
  const [selectedShipmentDetails, setSelectedShipmentDetails] =
    useState<ShipmentDetails | null>(null);
  const [trackingDetails, setTrackingDetails] =
    useState<TrackingDetails | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>(initialShipments);
  const {
    isOpen: isOpenCancelShipmentDialogue,
    onOpen: onOpenCancelShipmentDialogue,
    onClose: onCloseCancelShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenMarkDeliveredShipmentDialogue,
    onOpen: onOpenMarkDeliveredShipmentDialogue,
    onClose: onCloseMarkDeliveredShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteShipmentDialogue,
    onOpen: onOpenDeleteShipmentDialogue,
    onClose: onCloseDeleteShipmentDialogue,
  } = useDisclosure();

  const {
    isOpen: isShipmentModalOpen,
    onOpen: onOpenShipmentModal,
    onClose: onCloseShipmentModal,
  } = useDisclosure();

  const [shipmentIdToAction, setShipmentIdToAction] = useState<string>("");
  const [isSelfDispatch, setIsSelfDispatch] =
    useState<boolean>(false);
  const bgColor = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const [refetchCooldowns, setRefetchCooldowns] = useState<
    Record<string, number>
  >({});
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const cooldownTime =
    Number(mobileConfigs?.orca_shipment_refetch_cooldown_seconds) * 1000 ||
    10000;
  const isShipmentDeletionEnabled = isMobileConfigFlagEnabled(
    mobileConfigs?.ff_enable_shipment_deletion,
  );

  const toast = useToast();

  const renderActionButton = (
    isVisible: boolean,
    opts: {
      label: string;
      ariaLabel: string;
      icon: JSX.Element;
      hoverColor: string;
      hoverBg: string;
      onClick: (e: React.MouseEvent) => void;
      isDisabled?: boolean;
    }
  ) => {
    if (!isVisible) return null;
    return (
      <Tooltip label={opts.label}>
        <IconButton
          aria-label={opts.ariaLabel}
          icon={opts.icon}
          size="sm"
          variant="ghost"
          color="gray.400"
          _hover={{
            color: opts.hoverColor,
            transform: "scale(1.15)",
            bg: opts.hoverBg,
          }}
          transition="all 0.2s ease"
          onClick={opts.onClick}
          isDisabled={opts.isDisabled}
        />
      </Tooltip>
    );
  };

  const getTrackingDetails = (shipment: Shipment): TrackingDetails => ({
    trackingUrl: shipment.deliveryBooking?.trackingUrl || "",
    awbNo: shipment.deliveryBooking?.providerOrderId || "",
    shipmentNo: shipment.shipmentId,
    status: shipment.deliveryTracking?.trackingStatus,
    lastUpdatedAt: shipment.deliveryTracking?.lastUpdatedAt,
  });

  useEffect(() => {
    setShipments(initialShipments);
  }, [initialShipments]);

  const handleViewShipment = async (shipment: Shipment) => {
    let latestShipment: Shipment = shipment;
    try {
      latestShipment = await getShipment(shipment.shipmentId, {
        populate: ["orders", "supplier", "customer"],
      });
    } catch (err: any) {
      toast({
        title: "Fetch Shipment ",
        description: err?.errorMsg,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setSelectedShipmentDetails(
      mapApiShipmentToShipmentDetails(latestShipment)
    );
    onOpenShipmentModal();
  };

  const updateShipment = async (shipmentId: string) => {
    try {
      const response = await getShipment(shipmentId);
      setShipments((prev) =>
        prev.map((shipment) =>
          shipment.shipmentId === shipmentId
            ? { ...shipment, ...response }
            : shipment
        )
      );
    } catch (error) {
      console.error("Failed to update shipment in updateShipment:", error);
      throw error;
    }
  };

  const handleRefetchShipment = async (shipmentId: string) => {
    const now = Date.now();
    const lastRefetch = refetchCooldowns[shipmentId] || 0;
    const timeSinceLastRefetch = now - lastRefetch;

    if (timeSinceLastRefetch < cooldownTime) {
      const remainingTime = Math.ceil(
        (cooldownTime - timeSinceLastRefetch) / 1000
      );
      toast({
        title: "Refetch Cooldown",
        description: `Please wait ${remainingTime} seconds before refetching this shipment again.`,
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      setRefetchCooldowns((prev) => ({ ...prev, [shipmentId]: now }));

      try {
        await getTracking(shipmentId);
      } catch (error) {
        console.log("Failed to get tracking in handleRefetchShipment:", error);
      }

      await updateShipment(shipmentId);
      toast({
        title: `Shipment ${shipmentId} updated successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Failed to refetch shipment details.",
        description:
          error instanceof Error ? error.message : "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      // Reset cooldown on error so user can retry
      setRefetchCooldowns((prev) => ({ ...prev, [shipmentId]: 0 }));
    }
  };

  const handleCancelShipment = async (shipmentId: string) => {
    try {
      const cancelResponse = await cancelShipment(shipmentId);
      if (cancelResponse.code !== 200) {
        throw new Error(cancelResponse.errorMsg);
      }

      toast({
        title: `Shipment ${shipmentId} cancelled successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      try {
        await updateShipment(shipmentId);
      } catch (error) {
        console.error(
          "Failed to update shipment in handleCancelShipment:",
          error
        );
      }
    } catch (error) {
      console.error(
        "Failed to cancel shipment in handleCancelShipment:",
        error
      );
      toast({
        title: `Failed to cancel shipment ${shipmentId}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleDeleteShipment = async (shipmentId: string) => {
    try {
      await deleteShipment(shipmentId);

      toast({
        title: `Shipment ${shipmentId} deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      setShipments((prev) => prev.filter((s) => s.shipmentId !== shipmentId));
    } catch (error) {
      toast({
        title: `Failed to delete shipment ${shipmentId}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleUpdateShipmentStatus = async (
    shipmentId: string,
    status: ShipmentStatus.DELIVERED | ShipmentStatus.CANCELLED
  ) => {
    try {
      await updateShipmentService(shipmentId, { status });
      toast({
        title: `Shipment ${shipmentId} marked as ${status}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      try {
        await updateShipment(shipmentId);
      } catch (error) {
        console.error(
          `Failed to update shipment in handleUpdateShipmentStatus:`,
          error
        );
      }
    } catch (error) {
      toast({
        title: `Failed to mark shipment ${shipmentId} as ${status}`,
        description:
          error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const openCancelShipmentDialog = (
    shipmentId: string,
    isSelfDispatch: boolean = false
  ) => {
    setShipmentIdToAction(shipmentId);
    setIsSelfDispatch(isSelfDispatch);
    onOpenCancelShipmentDialogue();
  };

  const openDeleteShipmentDialog = (shipmentId: string) => {
    setShipmentIdToAction(shipmentId);
    onOpenDeleteShipmentDialogue();
  };

  const openMarkDeliveredShipmentDialog = (
    shipmentId: string,
    isSelfDispatch: boolean = false
  ) => {
    setShipmentIdToAction(shipmentId);
    setIsSelfDispatch(isSelfDispatch);
    onOpenMarkDeliveredShipmentDialogue();
  };

  const handleConfirmedCancelShipment = () => {
    handleCancelShipment(shipmentIdToAction);
    onCloseCancelShipmentDialogue();
    onCloseShipmentModal();
  setShipmentIdToAction("");
    setIsSelfDispatch(false);
  };
  
  const handleMarkConsignmentDelivered = async (
    shipmentId: string,
    orderNo: string
  ) => {
    try {
      await markConsignmentDeliveredService(shipmentId, orderNo);
      toast({
        title: `Order ${orderNo} marked as delivered`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      await updateShipment(shipmentId);
    } catch (error) {
      toast({
        title: `Failed to mark ${orderNo} as delivered`,
        description: error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleConfirmedMarkDeliveredShipment = async () => {
    try {
      if (isSelfDispatch) {
        await updateShipmentService(shipmentIdToAction, {
          status: ShipmentStatus.DELIVERED,
          deliveryTracking: {
            timeline: {
              deliveredAt: new Date().toISOString(),
            },
          },
        });
        toast({
          title: `Shipment ${shipmentIdToAction} marked as DELIVERED`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        try {
          await updateShipment(shipmentIdToAction);
        } catch (error) {
          console.error(
            `Failed to update shipment in handleConfirmedMarkDeliveredShipment:`,
            error
          );
        }
      } else {
        await handleUpdateShipmentStatus(shipmentIdToAction, ShipmentStatus.DELIVERED);
      }
    } catch (error) {
      toast({
        title: `Failed to mark shipment ${shipmentIdToAction} as DELIVERED`,
        description: error instanceof Error ? error.message : "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      onCloseMarkDeliveredShipmentDialogue();
      onCloseShipmentModal();
      setShipmentIdToAction("");
      setIsSelfDispatch(false);
    }
  };

  const handleConfirmedDeleteShipment = () => {
    handleDeleteShipment(shipmentIdToAction);
    onCloseDeleteShipmentDialogue();
    onCloseShipmentModal();
    setShipmentIdToAction("");
  };

  return (
    <Box bg={bgColor}>
      <Table variant="simple" textAlign={"center"}>
        <Thead bg={headerBg} position="sticky" top={0} zIndex={10}>
          <Tr>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              <HStack spacing={2}>
                <Icon as={FaTruck} boxSize={3.5} color="orange.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Shipment Details
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  # Order Numbers
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={FaFileInvoice} boxSize={3} color="purple.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  # Supplier Invoices
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={FaFileInvoice} boxSize={3} color="purple.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  # Garaaz Invoices
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2}>
                <Icon as={FaBox} boxSize={3} color="blue.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Total Items & Value
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2}>
                <Icon as={FaTruck} boxSize={3.5} color="green.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Status
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={MdAccessTime} boxSize={3.5} color="yellow.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Pickup & Delivery Time
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={FaUser} boxSize={3.5} color="blue.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Rider Detail
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={FaMoneyBill} boxSize={3.5} color="purple.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Total Fare Amount
                </Text>
              </HStack>
            </Th>
            <Th
              px={6}
              py={3}
              fontSize="xs"
              fontWeight="medium"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              textAlign={"left"}
            >
              <HStack spacing={2} justifyContent={"flex-start"}>
                <Icon as={FaEllipsisH} boxSize={3.5} color="red.500" />
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="0.025em"
                >
                  Actions
                </Text>
              </HStack>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {shipments
            .map((shipment) => {
              const createdAt = `${formatTime(
                shipment.createdAt
              )} - ${formatDateForNotification(shipment.createdAt)}`;
              const estimatedPickupTime = shipment.deliveryBooking
                ?.estimatedPickupTime
                ? `${formatTime(
                    shipment.deliveryBooking?.estimatedPickupTime
                  )} - ${formatDateForNotification(
                    shipment.deliveryBooking?.estimatedPickupTime
                  )}`
                : "-";
              const pickupTime = shipment.deliveryTracking?.timeline?.pickedUpAt
                ? `${formatTime(
                    shipment.deliveryTracking?.timeline?.pickedUpAt
                  )} - ${formatDateForNotification(
                    shipment.deliveryTracking?.timeline?.pickedUpAt
                  )}`
                : "-";
              const deliveryTime = shipment.deliveryTracking?.timeline
                ?.deliveredAt
                ? `${formatTime(
                    shipment.deliveryTracking?.timeline?.deliveredAt
                  )} - ${formatDateForNotification(
                    shipment.deliveryTracking?.timeline?.deliveredAt
                  )}`
                : "-";

              const estimatedFare = shipment.deliveryBooking?.estimatedFare
                ? formatCurrency(
                    (shipment.deliveryBooking.estimatedFare.minorAmount || 0) /
                      100
                  )
                : "-";
              const actualFare = shipment.deliveryBooking?.actualFare
                ? formatCurrency(
                    (shipment.deliveryBooking.actualFare.minorAmount || 0) / 100
                  )
                : "-";
              const deliveryMode = (shipment as any).deliveryMode as DeliveryModes | undefined;
              const displayShipmentId = (() => {
                const id = shipment.shipmentId;
                if (id.length <= 8) return id; // If too short, show as is

                const start = id.substring(0, 6);
                const end = id.substring(id.length - 10);
                return `${start}....${end}`;
              })();

              const rows = [
                <Tr
                  key={`shipment-${shipment.shipmentId}`}
                  _hover={{ bg: hoverBg }}
                >
                  {/* Shipment Details */}
                  <Td px={6} py={4}>
                    <VStack align="start" spacing={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="blue.600"
                        title={shipment.shipmentId}
                        display={"flex"}
                        flexDirection={"row"}
                        gap={1}
                      >
                        <Text fontSize="sm" color="gray.600" mt={-0.5}>
                          {displayShipmentId}
                        </Text>
                        <GaraazCopyText
                          text={shipment.shipmentId}
                          popOverMessage={"Copied"}
                        />
                      </Text>
                      {shipment.deliveryBooking?.providerOrderId && (
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color="gray.600"
                          title={shipment.deliveryBooking?.providerOrderId}
                          display={"flex"}
                          flexDirection={"row"}
                          gap={1}
                          mt={1}
                        >
                          <Text fontSize="xs" color="gray.600" mt={-1}>
                            {shipment.deliveryBooking?.providerOrderId}
                          </Text>
                          <GaraazCopyText
                            text={shipment.deliveryBooking?.providerOrderId}
                            popOverMessage={"Copied"}
                          />
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.500">
                        {createdAt}
                      </Text>
                      {deliveryMode && (
                        <DeliveryModeBadge mode={deliveryMode} />
                      )}
                    </VStack>
                  </Td>

                  {/* Order Numbers */}
                  <Td px={6} py={4} textAlign="left">
                    <VStack spacing={1} align="start">
                      {shipment.orderNumbers?.map((order, index) => {
                        const podStatus = getDeliveryStopStatus(shipment, order);
                        const isDelivered = podStatus === "DELIVERED";

                        return (
                        <Box
                          key={index}
                          flexDirection={"row"}
                          display={"flex"}
                          gap={1}
                          mt={1}
                          alignItems="center"
                        >
                          <Text fontSize="xs" color="gray.600" mt={-0.5}>
                            {order}
                          </Text>
                          {isMultiStopTrip(shipment) && (
                            <Text
                              fontSize="xs"
                              color={isDelivered ? "green.600" : "orange.600"}
                              fontWeight="600"
                            >
                              {isDelivered ? "Delivered" : "Pending"}
                            </Text>
                          )}
                          {isMultiStopTrip(shipment) &&
                            !isDelivered &&
                            shipment.status !== ShipmentStatus.DELIVERED &&
                            shipment.status !== ShipmentStatus.CANCELLED && (
                              <Button
                                size="xs"
                                colorScheme="green"
                                variant="outline"
                                cursor="pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkConsignmentDelivered(
                                    shipment.shipmentId,
                                    order
                                  );
                                }}
                              >
                                Mark Delivered
                              </Button>
                            )}
                          <GaraazCopyText
                            text={order}
                            popOverMessage={"Copied"}
                            key={index}
                          />
                        </Box>
                        );
                      })}
                    </VStack>
                  </Td>

                  <Td px={6} py={4} textAlign="left">
                    <VStack spacing={1} align="start">
                      {shipment.supplierInvoiceNumbers &&
                      shipment.supplierInvoiceNumbers.length > 0 ? (
                        shipment.supplierInvoiceNumbers.map(
                          (invoice, index) => (
                            <Box
                              key={index}
                              flexDirection={"row"}
                              display={"flex"}
                              gap={1}
                              mt={1}
                            >
                              <Text fontSize="xs" color="gray.600" mt={-0.5}>
                                {invoice}
                              </Text>
                              <GaraazCopyText
                                text={invoice}
                                popOverMessage={"Invoice Copied"}
                                key={index}
                              />
                            </Box>
                          )
                        )
                      ) : (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic">
                          No invoices
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td px={6} py={4} textAlign="left">
                    <VStack spacing={1} align="start">
                      {shipment.invoiceNumbers &&
                      shipment.invoiceNumbers.length > 0 ? (
                        shipment.invoiceNumbers.map((invoice, index) => (
                          <Box
                            key={index}
                            flexDirection={"row"}
                            display={"flex"}
                            gap={1}
                            mt={1}
                          >
                            <Text fontSize="xs" color="gray.600" mt={-0.5}>
                              {invoice}
                            </Text>
                            <GaraazCopyText
                              text={invoice}
                              popOverMessage={"Invoice Copied"}
                              key={index}
                            />
                          </Box>
                        ))
                      ) : (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic">
                          No invoices
                        </Text>
                      )}
                    </VStack>
                  </Td>

                  {/* Total Items & Value */}
                  <Td px={6} py={4} textAlign="left">
                    <VStack spacing={1} align="start">
                      <Text
                        fontSize="sm"
                        color="gray.900"
                        fontWeight="medium"
                        textAlign="left"
                      >
                        <Text as="span" color="blue.600" fontSize={"sm"}>
                          {shipment.totalItems || 0} items,
                          {shipment.totalQuantity || 0} Qty
                        </Text>
                      </Text>
                      <Text
                        fontSize="sm"
                        color="green.600"
                        fontWeight="semibold"
                        textAlign="left"
                      >
                        ₹{(shipment.totalValue || 0).toLocaleString()}
                      </Text>
                    </VStack>
                  </Td>

                  {/* Status */}
                  <Td px={6} py={4} textAlign="left">
                    <VStack align="start" spacing={1}>
                      <GetStatusBadge status={shipment.status} />
                    </VStack>
                  </Td>

                  {/* Pickup & Delivery */}
                  <Td px={6} py={4}>
                    <VStack spacing={1} align="start">
                      <Box
                        display="grid"
                        gridTemplateColumns="1fr auto 1.5fr"
                        gap={2}
                        w="100%"
                        alignItems="center"
                      >
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          Exp. Pickup
                        </Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          :
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          {estimatedPickupTime}
                        </Text>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          PickedUp At
                        </Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          :
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          {pickupTime}
                        </Text>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          Delivered At
                        </Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          :
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          {deliveryTime}
                        </Text>
                      </Box>
                    </VStack>
                  </Td>

                  {/* Rider Detail */}
                  <Td px={6} py={4}>
                    <VStack spacing={1} align="start">
                      {shipment.deliveryTracking?.deliveryAgent ? (
                        <>
                          <Text
                            fontSize="xs"
                            color="gray.700"
                            fontWeight="medium"
                          >
                            {shipment.deliveryTracking.deliveryAgent.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {
                              shipment.deliveryTracking.deliveryAgent
                                .phoneNumber
                            }
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {
                              shipment.deliveryTracking.deliveryAgent
                                .alternatePhoneNumber
                            }
                          </Text>

                          {/* Vehicle Details */}
                          {shipment.deliveryTracking.deliveryAgent
                            .vehicleDetails && (
                            <>
                              <HStack spacing={2} align="center">
                                {shipment.deliveryTracking.deliveryAgent
                                  .vehicleDetails.vehicleType ===
                                  VehicleType.TWO_WHEELER && (
                                  <Icon
                                    as={FaMotorcycle}
                                    boxSize={3}
                                    color="blue.600"
                                    mt={0}
                                  />
                                )}
                                <Text fontSize="xs" color="gray.600">
                                  {shipment.deliveryTracking.deliveryAgent
                                    .vehicleDetails.vehicleNumber || "N/A"}
                                </Text>
                              </HStack>
                            </>
                          )}
                        </>
                      ) : (
                        <Text fontSize="sm" color="gray.700">
                          -
                        </Text>
                      )}
                    </VStack>
                  </Td>

                  {/* Total Fare Amount */}
                  <Td px={6} py={4}>
                    <VStack spacing={1} align="start">
                      <Box
                        display="grid"
                        gridTemplateColumns="1fr auto 1fr"
                        gap={2}
                        w="100%"
                        alignItems="center"
                      >
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          Est. Fare
                        </Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          :
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          {estimatedFare}
                        </Text>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                          textAlign="left"
                        >
                          Actual Fare
                        </Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          :
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          {actualFare}
                        </Text>
                      </Box>
                    </VStack>
                  </Td>

                  {/* Actions */}
                  <Td px={4} py={4}>
                    <HStack spacing={1} justifyContent="flex-start">
                      {renderActionButton(true, {
                        label: "View Shipment Details",
                        ariaLabel: "View Details",
                        icon: <FaEye />,
                        hoverColor: "blue.600",
                        hoverBg: "blue.50",
                        onClick: (e) => {
                          e.stopPropagation();
                          handleViewShipment(shipment);
                        },
                      })}

                      {renderActionButton(
                        Boolean(
                          shipment.deliveryBooking?.trackingUrl
                        ),
                        {
                          label: "Track Shipment",
                          ariaLabel: "Track Shipment",
                          icon: <FaRoute />,
                          hoverColor: "green.600",
                          hoverBg: "green.50",
                          onClick: (e) => {
                            e.stopPropagation();
                            setTrackingDetails(getTrackingDetails(shipment));
                          },
                        }
                      )}

                      {renderActionButton(
                        !!shipment.deliveryBooking?.providerOrderId,
                        {
                          label: "Refetch Details",
                          ariaLabel: "Refetch",
                          icon: <FaSync />,
                          hoverColor: "blue.600",
                          hoverBg: "blue.50",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleRefetchShipment(shipment.shipmentId);
                          },
                        }
                      )}

                      {renderActionButton(
                        !isMultiStopTrip(shipment) &&
                          !shipment.deliveryBooking?.providerOrderId &&
                          shipment.status !== ShipmentStatus.DELIVERED &&
                          shipment.status !== ShipmentStatus.CANCELLED,
                        {
                          label: "Mark as Delivered",
                          ariaLabel: "Mark Delivered",
                          icon: <FcShipped color="gray.50"/>,
                          hoverColor: "green.600",
                          hoverBg: "green.50",
                          onClick: (e) => {
                            e.stopPropagation();
                            openMarkDeliveredShipmentDialog(
                              shipment.shipmentId,
                              !shipment.deliveryBooking?.providerOrderId
                            );
                          },
                        }
                      )}

                      {renderActionButton(
                        shipment.status !== ShipmentStatus.DELIVERED &&
                          shipment.status !== ShipmentStatus.CANCELLED &&
                          cancelableShipmentStatuses.includes(shipment.status),
                        {
                          label: "Cancel Shipment",
                          ariaLabel: "Cancel",
                          icon: <FaTimes />,
                          hoverColor: "orange.600",
                          hoverBg: "orange.50",
                          onClick: (e) => {
                            e.stopPropagation();
                            openCancelShipmentDialog(
                              shipment.shipmentId,
                              !shipment.deliveryBooking?.providerOrderId
                            );
                          },
                        }
                      )}

                      {renderActionButton(
                        deletableShipmentStatuses.includes(shipment.status),
                        {
                          label: "Delete Shipment",
                          ariaLabel: "Delete",
                          icon: <FaTrash />,
                          hoverColor: "red.600",
                          hoverBg: "red.50",
                          isDisabled: !isShipmentDeletionEnabled,
                          onClick: (e) => {
                            e.stopPropagation();
                            if (!isShipmentDeletionEnabled) {
                              return;
                            }
                            openDeleteShipmentDialog(shipment.shipmentId);
                          },
                        }
                      )}
                    </HStack>
                  </Td>
                </Tr>,
              ];

              return rows;
            })
            .flat()}
        </Tbody>
      </Table>

      {shipments.length === 0 && (
        <Box textAlign="center" py={12}>
          <Box
            w={16}
            h={16}
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
            mb={4}
            bg="gray.100"
            borderRadius="full"
          >
            <Icon as={FaTruck} boxSize={8} color="gray.400" />
          </Box>
          <Heading size="lg" color="gray.900" mb={2}>
            No shipments found
          </Heading>
          <Text color="gray.500">Try adjusting your search or filters</Text>
        </Box>
      )}

      <TrackingModal
        isOpen={!!trackingDetails}
        onClose={() => setTrackingDetails(null)}
        trackingDetails={
          trackingDetails || {
            trackingUrl: "",
            awbNo: "",
            shipmentNo: "",
            status: PorterDeliveryTrackingStatus.OPEN,
          }
        }
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to mark shipment ${shipmentIdToAction} as delivered? This action cannot be undone.`}
        alertDialogueHeaderText="Confirm Delivery"
        onSubmitBtn={handleConfirmedMarkDeliveredShipment}
        isOpen={isOpenMarkDeliveredShipmentDialogue}
        onClose={onCloseMarkDeliveredShipmentDialogue}
        onOpen={onOpenMarkDeliveredShipmentDialogue}
        submitBtnText="Mark as Delivered"
        leftBtnText="Cancel"
        cancelBtnColorScheme="gray"
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to cancel shipment ${shipmentIdToAction}?\nThe linked order(s) will return to Ready to Dispatch, allowing you to create new shipments and dispatch them again. This action cannot be undone.`}
        alertDialogueHeaderText="Confirm Cancellation"
        onSubmitBtn={handleConfirmedCancelShipment}
        isOpen={isOpenCancelShipmentDialogue}
        onClose={onCloseCancelShipmentDialogue}
        onOpen={onOpenCancelShipmentDialogue}
        submitBtnText="Cancel Shipment"
        leftBtnText="Keep Shipment"
        submitBtnColorScheme="red"
        cancelBtnColorScheme="gray"
      />

      <GaraazAlertDialogue
        alertBodyText={`Are you sure you want to delete shipment ${shipmentIdToAction}? \nThis action cannot be undone.`}
        alertDialogueHeaderText="Confirm Deletion"
        onSubmitBtn={handleConfirmedDeleteShipment}
        isOpen={isOpenDeleteShipmentDialogue}
        onClose={onCloseDeleteShipmentDialogue}
        onOpen={onOpenDeleteShipmentDialogue}
        submitBtnText="Delete Shipment"
        leftBtnText="Keep Shipment"
        submitBtnColorScheme="red"
        cancelBtnColorScheme="gray"
      />

      <ShipmentDetailsModal
        isOpen={isShipmentModalOpen}
        onClose={onCloseShipmentModal}
        shipment={selectedShipmentDetails}
        onCancelShipment={(shipmentId, isSelfDispatch) => {
          openCancelShipmentDialog(shipmentId, isSelfDispatch);
        }}
        onDeleteShipment={(shipmentId) => {
          openDeleteShipmentDialog(shipmentId);
        }}
        isDeleteShipmentDisabled={!isShipmentDeletionEnabled}
        onMarkDeliveredShipment={(shipmentId, isSelfDispatch) => {
          openMarkDeliveredShipmentDialog(shipmentId, isSelfDispatch);
        }}
      />
    </Box>
  );
}
